import React, { useState, useEffect } from 'react';
import { SafeAreaView, View, StyleSheet, AsyncStorage, FlatList } from 'react-native';
import { Text, Button, Card, Input, CheckBox } from 'react-native-elements';
import RNPickerSelect from 'react-native-picker-select';
import { RenderType, RenderDescription, Tools } from '../../utils/types/Tools';
import { AdminRenderer } from '../../utils/Renderer';
import { ScrollView } from 'react-native-gesture-handler';
import firebase from 'firebase';
import { connect } from 'react-redux';
import { RootState } from '../../redux';
import { User } from '../../utils/types/Users';
import Firebase from '../../components/FirebaseContext';
import { SuccessToast, FailureToast } from '../../utils';
import { RouteProp } from '@react-navigation/native';
import { StackScreenProps } from '@react-navigation/stack';
import { ToolStackParamList } from './ToolStack';
// import hospital from '../../utils/Hospital.json';
type NavProps = StackScreenProps<ToolStackParamList, 'Create'>;

interface CreateToolProps extends NavProps {
	user: User;
	route: RouteProp<ToolStackParamList, 'Create'>;
}

function CreateTool({ user, route }: CreateToolProps): JSX.Element {
	const { toolId } = route.params;
	const firestore = Firebase.firestore();
	const [toolTitle, setToolTitle] = useState('');
	const [toolSubTitle, setToolSubTitle] = useState('');
	const [allowNumbering, setAllowNumbering] = useState(true);
	const [beginNumberingAt, setBeginNumberingAt] = useState<string>('1');
	const [savedQuestions, setSavedQuestions] = useState<Array<RenderDescription>>([]);
	const [question, setQuestion] = useState('');
	const [options, setOptions] = useState('');
	const [optionsType, setOptionsType] = useState<RenderType>('text');
	const [isRequired, setIsRequired] = useState(false);
	const [editIndex, setEditIndex] = useState<number>();
	const [loading, setLoading] = useState(false);
	// const [injectHospital, setInjectHospital] = useState(true);

	const resetAddQuestion = () => {
		setQuestion('');
		setOptions('');
		setOptionsType('text');
		setIsRequired(false);
		setEditIndex(undefined);
	};

	const resetToolTitle = () => {
		setToolTitle('');
		setToolSubTitle('');
		setAllowNumbering(true);
		setBeginNumberingAt('');
	};

	const [toolEdit, setToolEdit] = useState<Tools>();
	// get tool from async store if tooId has value
	useEffect(() => {
		if (toolId) {
			if (toolId == 'new') {
				setToolTitle('');
				setToolSubTitle('');
				setAllowNumbering(true);
				setBeginNumberingAt('1');
				setSavedQuestions([]);
			} else {
				AsyncStorage.getItem(toolId).then((value) => {
					if (value) {
						const tool = JSON.parse(value) as Tools;
						setToolTitle(tool.name);
						setToolSubTitle(tool.description);
						setAllowNumbering(tool.allowNumbering);
						setBeginNumberingAt(tool.beginNumberingAt.toString());
						setSavedQuestions(tool.questions);
						setToolEdit(tool);
					}
				});
				AsyncStorage.removeItem(toolId);
			}
		}
	}, [toolId]);

	const saveTool = async () => {
		setLoading(true);
		const tool: Tools = {
			name: toolTitle,
			description: toolSubTitle.length > 0 ? toolSubTitle : ' ',
			id: `LASHMA-T${Date.now()}`,
			allowNumbering: allowNumbering,
			beginNumberingAt: Number(beginNumberingAt),
			questions: savedQuestions,
			createdAt: firebase.firestore.FieldValue.serverTimestamp(),
			createdBy: user.uid,
		};

		try {
			await firestore.collection('tools').add(tool);
			SuccessToast('Tool saved!');
			setSavedQuestions([]);
			resetToolTitle();
			setLoading(false);
		} catch (error) {
			FailureToast('Failed to save tool');
			setLoading(false);
		}
	};

	const saveEditedTool = async () => {
		if (toolEdit) {
			setLoading(true);
			toolEdit.name = toolTitle;
			toolEdit.description = toolSubTitle.length > 0 ? toolSubTitle : ' ';
			toolEdit.allowNumbering = allowNumbering;
			toolEdit.beginNumberingAt = Number(beginNumberingAt);
			toolEdit.questions = savedQuestions;
			toolEdit.createdAt = new Date(toolEdit.createdAt as string); // compatible with serverTimeStamp
			try {
				await firestore.collection('tools').doc(toolId).set(toolEdit);
				SuccessToast(`Tool ${toolEdit.id} updated!`);
				setSavedQuestions([]);
				resetToolTitle();
				setLoading(false);
			} catch (error) {
				FailureToast('Failed to update tool');
				setLoading(false);
			}
		}
	};

	return (
		<SafeAreaView style={{ flex: 1, flexDirection: 'row' }}>
			<View style={{ flex: 1 }}>
				<Text style={{ fontWeight: '700', marginBottom: 20, textAlign: 'center', marginTop: 20 }} h3>
					Tool Creator
				</Text>
				<Text style={{ fontWeight: '700', marginBottom: 28, textAlign: 'center', marginTop: 10 }} h4>
					{toolTitle ? toolTitle : 'Give this tool a title'}
				</Text>
				<Text
					style={{
						fontWeight: '700',
						marginBottom: 28,
						textAlign: 'center',
						marginTop: 10,
						maxWidth: 720,
						alignSelf: 'center',
					}}
				>
					{toolSubTitle ? toolSubTitle : 'Optionally describe this tool'}
				</Text>
				<FlatList
					style={{ marginVertical: 10, paddingHorizontal: 50 }}
					data={savedQuestions}
					renderItem={({ item }) => (
						<AdminRenderer
							question={item}
							index={savedQuestions.indexOf(item)}
							allowNumbering={allowNumbering}
							beginNumberingAt={
								Number(beginNumberingAt) > 9 || Number(beginNumberingAt) < 1 ? 1 : Number(beginNumberingAt)
							}
							edit={(index) => {
								setEditIndex(index);
								setQuestion(savedQuestions[index].content);
								setOptions(savedQuestions[index].options.join('; '));
								setOptionsType(savedQuestions[index].type);
								setIsRequired(savedQuestions[index].required);
							}}
							del={(index) => {
								savedQuestions.splice(index, 1);
								const newQuestions = Object.assign([], savedQuestions);
								setSavedQuestions(newQuestions);
								resetAddQuestion();
							}}
							// drag={drag}
						/>
					)}
					// onDragEnd={({ data }) => setSavedQuestions(data)}
					keyExtractor={(item) => item.content.replace(/[^\w]/gi, '')}
				/>
			</View>
			<ScrollView style={{ flex: 0.4, backgroundColor: '#45a6dd' }}>
				<Card containerStyle={{ marginBottom: 20 }} title="Add Tool Title">
					<Input
						placeholder="Enter tool title"
						value={toolTitle}
						onChangeText={(text) => {
							setToolTitle(text);
						}}
					/>
					<Input
						placeholder="Enter tool sub title"
						value={toolSubTitle}
						onChangeText={(text) => {
							setToolSubTitle(text);
						}}
					/>
					<View style={{ flexDirection: 'row' }}>
						<CheckBox
							title="Allow Numbering?"
							checked={allowNumbering}
							containerStyle={{ borderWidth: 0, backgroundColor: '#00000000' }}
							onPress={() => {
								setAllowNumbering(!allowNumbering);
							}}
						/>
						{allowNumbering ? (
							<Input
								placeholder="Start at"
								value={beginNumberingAt}
								keyboardType="number-pad"
								onChangeText={(text) => {
									setBeginNumberingAt(text);
								}}
								containerStyle={{ width: 100 }}
								inputStyle={{ width: 100 }}
							/>
						) : (
							<></>
						)}
					</View>
				</Card>
				<Card containerStyle={{ marginBottom: 20 }} title={editIndex != undefined ? 'Edit question' : 'Add Question'}>
					<Input
						placeholder="Enter question here"
						value={question}
						onChangeText={(text) => {
							setQuestion(text);
						}}
					/>
					<View style={{ paddingHorizontal: 10 }}>
						<Text style={{ fontSize: 12 }}>Choose option type</Text>
						<RNPickerSelect
							value={optionsType}
							items={[
								{ label: 'Text', value: 'text' },
								{ label: 'Single choice', value: 'single-choice' },
								{ label: 'Multi choice', value: 'multi-choice' },
								{ label: 'Number', value: 'number' },
								{ label: 'Date', value: 'date' },
								{ label: 'List', value: 'list' },
							]}
							onValueChange={(itemValue) => {
								setOptionsType(itemValue);
							}}
							style={PickerStyle}
						/>
					</View>
					{optionsType == 'list' || optionsType == 'single-choice' || optionsType == 'multi-choice' ? (
						<Input
							placeholder="Enter options - delimit by ;"
							value={options}
							onChangeText={(text) => {
								setOptions(text);
							}}
						/>
					) : (
						<></>
					)}
					<CheckBox
						title="Required?"
						checked={isRequired}
						containerStyle={{ borderWidth: 0, backgroundColor: '#00000000' }}
						onPress={() => {
							setIsRequired(!isRequired);
						}}
					/>
					<Button
						title={editIndex != undefined ? 'Save Modifications' : 'Add to Questions'}
						buttonStyle={{ backgroundColor: '#275036', paddingVertical: 6 }}
						containerStyle={{ marginTop: 20 }}
						onPress={() => {
							const q: RenderDescription = {
								content: question,
								type: optionsType,
								required: isRequired,
								options: options.split(';').map((o) => o.trim()),
								selections: [],
							};
							if (editIndex != undefined) {
								savedQuestions.splice(editIndex, 1, q);
								setSavedQuestions(savedQuestions);
							} else {
								const nq = [...savedQuestions, ...[q]];
								setSavedQuestions(nq);
							}
							resetAddQuestion();
						}}
					/>
				</Card>
				<Card title="Done Editing" containerStyle={{ marginBottom: 40 }}>
					<Button
						title="Save to database"
						buttonStyle={{ backgroundColor: '#275036', paddingVertical: 6 }}
						containerStyle={{ marginTop: 20 }}
						onPress={async () => {
							if (toolEdit == undefined) {
								// save new tool
								saveTool();
							}
							if (toolEdit) {
								// edit tool
								saveEditedTool();
							}
						}}
						loading={loading}
					/>
				</Card>
			</ScrollView>
		</SafeAreaView>
	);
}

const PickerStyle = StyleSheet.create({
	inputIOS: {
		paddingVertical: 12,
		paddingHorizontal: 10,
		borderWidth: 0,
		borderColor: 'gray',
		borderRadius: 4,
		color: 'black',
		paddingRight: 30, // to ensure the text is never behind the icon
	},
	inputAndroid: {
		paddingHorizontal: 10,
		paddingVertical: 8,
		borderWidth: 0,
		borderColor: '#c5c5c5',
		borderBottomWidth: 1,
		borderRadius: 0,
		color: 'black',
		paddingRight: 30, // to ensure the text is never behind the icon
	},
});

const mapStateToProps = (state: RootState) => {
	const { user } = state;
	return {
		user,
	};
};

export default connect(mapStateToProps)(CreateTool);
